import type { CSS } from '@stitches/react';
import React, { memo, useState } from 'react';

import {
  ButtonLinkProps,
  CarSelectionButton,
} from '@/components/sections/product-compare/car-selection-card/car-selection-button';
import { CarSelectionDetail } from '@/components/sections/product-compare/car-selection-card/car-selection-detail';
import {
  CarSelectionPrice,
  TooltipProps,
} from '@/components/sections/product-compare/car-selection-card/car-selection-price';
import { CarSelectionSlider } from '@/components/sections/product-compare/car-selection-card/car-selection-slider';
import { Text } from '@/components/shared/element/text';
import { IMAGE_VIEW, ModelImageProps } from '@/components/shared/model-slider';
import { DEFAULT_SEL_LABEL } from '@/contents/lcv/lcv-compare';
import { LcvModel } from '@/services/contentstack/models/lcv-model';
import { LcvModelColor } from '@/services/contentstack/models/lcv-model-color';

export type CarModel = LcvModel & {
  modelName?: string;
  modelCategory?: string;
  price?: number;
  image?: string;
  modelColor?: LcvModelColor;
};
interface CarSelectionCardProps {
  /**
   * Additional CSS styles (Apply All)
   */
  css?: CSS;
  /**
   * Index of the card in the list.
   */
  index: number;
  /**
   * The name of the car selection card.
   */
  name?: string;
  /**
   * The model data for the car selection card.
   */
  model: CarModel;
  /**
   * Whether to show the color selection.
   */
  showColor?: boolean;
  /**
   * The aspect ratio of the image.
   */
  ratio?: '4:3' | '16:9';
  /**
   * Whether to show the prefix price.
   */
  isShowPrefixPrice?: boolean;
  /**
   * The tooltip data for the price.
   */
  tooltip?: TooltipProps;
  /**
   * The remark price for the car.
   */
  remarkPrice?: string;
  /**
   * The button link for the car selection card.
   */
  buttonLink: ButtonLinkProps;
  /**
   * The active index of the slider.
   */
  activeSliderIndex?: number;
  /**
   * Whether to show the badge.
   */
  showBadge?: boolean;
  /**
   * The callback function for changing the color.
   */
  onChangeColor?: (color: LcvModelColor) => void;
  /**
   * The callback function for changing the slide.
   */
  onChangeSlide?: (swiper: any, image: ModelImageProps) => void;
  /**
   * The callback function for clicking the slide.
   */
  onClickSlide?: (event: MouseEvent | TouchEvent | PointerEvent) => void;
}

export const CarSelectionCard: React.FC<CarSelectionCardProps> = memo(
  ({
    css,
    index,
    name,
    model,
    tooltip,
    remarkPrice,
    buttonLink,
    isShowPrefixPrice,
    onChangeColor,
    onClickSlide,
    onChangeSlide,
    showColor = true,
    activeSliderIndex = 0,
    ratio = '16:9',
    showBadge = false,
  }) => {
    const [isShowColor, setIsShowColor] = useState<boolean>(!!showColor);
    const onHandleChangeSlide = (swiper: any, image: ModelImageProps) => {
      if (!showColor) return;
      setIsShowColor(image.view !== IMAGE_VIEW.INTERIOR);
      onChangeSlide && onChangeSlide(swiper, image);
    };

    return (
      <>
        <CarSelectionSlider
          css={css}
          name={name}
          model={model}
          currentIndex={index}
          activeSliderIndex={activeSliderIndex}
          ratio={ratio}
          showBadge={showBadge}
          onChange={onHandleChangeSlide}
          onClick={onClickSlide}
        />

        {model?.price >= 0 ? (
          <>
            <CarSelectionDetail
              css={css}
              index={index}
              model={model}
              showColor={isShowColor}
              disabledColor={!!showColor}
              onChangeModelColor={onChangeColor}
            />
            <CarSelectionPrice
              css={css}
              index={index}
              model={model}
              tooltip={tooltip}
              prefix={isShowPrefixPrice}
              remark={remarkPrice}
            />
            <CarSelectionButton
              css={css}
              modelName={model?.modelName || model?.name}
              link={buttonLink}
              index={index}
            />
          </>
        ) : (
          <Text
            css={{ textAlign: 'center', gridRow: '2 / -1', ...css }}
            size="text-body-1"
            font="bold"
            variant="text-gray-light"
            data-test={`compare_noSelected_${index}`}
          >
            {model?.modelCategory || DEFAULT_SEL_LABEL}
          </Text>
        )}
      </>
    );
  }
);

CarSelectionCard.displayName = 'CarSelectionCard';
